import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	CIRCLE_POLYGON_STEPS,
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const SECTOR_DRAWER_SOURCE_ID = "sector-drawer-source"

enum SectorDrawerLayerId {
	CIRCLE = "sector-drawer-circle-layer",
	LINE = "sector-drawer-line-layer",
	FILL = "sector-drawer-fill-layer",
}

interface SectorDrawerProps {
	onComplete?: (sector: Polygon) => void
}

export const SectorDrawer = memo<SectorDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)
	const [tempPoint, setTempPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
		setTempPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) {
				setFirstPoint(point)
			} else if (!secondPoint) {
				setSecondPoint(point)
			} else {
				const bearing1 = turf.bearing(firstPoint, secondPoint)
				const bearing2 = turf.bearing(firstPoint, point)
				const radius = turf.distance(firstPoint, secondPoint)

				const sector = turf.sector(firstPoint, radius, bearing1, bearing2, { steps: CIRCLE_POLYGON_STEPS })

				onComplete?.(sector.geometry)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint) return

			setTempPoint(point)
		},
		[firstPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection>(() => {
		const features: Feature[] = []

		if (firstPoint) {
			features.push(firstPoint)
		}

		if (secondPoint) {
			features.push(secondPoint)
		}

		if (!secondPoint && tempPoint) {
			features.push(tempPoint)
		}

		if (firstPoint && !secondPoint && tempPoint) {
			features.push(turf.lineString([firstPoint.geometry.coordinates, tempPoint.geometry.coordinates]))
		}

		if (firstPoint && secondPoint && tempPoint) {
			const bearing1 = turf.bearing(firstPoint, secondPoint)
			const bearing2 = turf.bearing(firstPoint, tempPoint)
			const radius = turf.distance(firstPoint, secondPoint)

			const sector = turf.sector(firstPoint, radius, bearing1, bearing2, { steps: CIRCLE_POLYGON_STEPS })

			features.push(turf.destination(firstPoint, radius, bearing2))
			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(sector))
			features.push(sector)
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint, tempPoint])

	return (
		<Source id={SECTOR_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={SectorDrawerLayerId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={SectorDrawerLayerId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={SectorDrawerLayerId.CIRCLE}
			/>
			<Layer
				id={SectorDrawerLayerId.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={SectorDrawerLayerId.LINE}
			/>
		</Source>
	)
})
SectorDrawer.displayName = "SectorDrawer"
